import { useState } from "react";
import { AUTH_PROVIDERS, useT, type AuthProviderId } from "@reciperoll/core";
import { supabase } from "../lib/supabase";

// Brand marks. Apple uses currentColor so it reads in both themes; the rest keep
// their brand colours (convention for social buttons on a neutral surface).
export function ProviderIcon({ id }: { id: AuthProviderId }) {
  const cls = "w-[18px] h-[18px] shrink-0";
  switch (id) {
    case "google":
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      );
    case "apple":
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M16.36 12.78c-.03-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.82-.81-3-.79-1.54.02-2.96.9-3.75 2.28-1.6 2.78-.41 6.89 1.15 9.14.76 1.1 1.67 2.34 2.86 2.29 1.15-.05 1.58-.74 2.97-.74 1.39 0 1.78.74 3 .72 1.24-.02 2.02-1.12 2.78-2.23.88-1.28 1.24-2.52 1.26-2.58-.03-.01-2.42-.93-2.44-3.68zM14.13 6.02c.64-.77 1.07-1.85.95-2.92-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.78-.97 2.83 1.03.08 2.07-.52 2.71-1.29z" />
        </svg>
      );
    case "azure":
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#F25022" d="M1 1h10v10H1z" />
          <path fill="#7FBA00" d="M13 1h10v10H13z" />
          <path fill="#00A4EF" d="M1 13h10v10H1z" />
          <path fill="#FFB900" d="M13 13h10v10H13z" />
        </svg>
      );
  }
}

// One button per social provider. Each kicks off a Supabase OAuth redirect back to
// the site root, where detectSessionInUrl exchanges the code and AuthGate swaps in
// the app. Coexists with email/password sign-in.
export function SocialAuthButtons() {
  const t = useT();
  const [busy, setBusy] = useState<AuthProviderId | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function signIn(provider: AuthProviderId) {
    setBusy(provider);
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/` },
    });
    // On success the browser navigates to the provider; only reached on error.
    if (error) {
      setError(error.message);
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {AUTH_PROVIDERS.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => signIn(p.id)}
          disabled={busy !== null}
          className="flex items-center justify-center gap-2.5 rounded-lg border border-line bg-surface px-4 py-2.5 text-sm font-semibold text-ink transition hover:bg-surface-2 disabled:opacity-60 cursor-pointer disabled:cursor-default"
        >
          <ProviderIcon id={p.id} />
          {busy === p.id ? t("auth.redirecting") : p.label}
        </button>
      ))}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
